import { useState, useEffect } from 'react'
import { DollarSign, TrendingUp, Users, Target } from 'lucide-react'
import MetricCard from '@/components/MetricCard'
import KPIDetailModal from '@/components/KPIDetailModal'

interface KPI {
  id: number
  name: string
  description: string
  current_value: number
  target_value: number
  unit: string
  trend: string
  change_percentage: number
  period: string
  category: string
}

interface OKRObjective {
  id: number
  title: string
  priority: string
  keyResults: any[]
}

export default function DashboardPage() {
  const [kpis, setKpis] = useState<Record<string, KPI[]>>({})
  const [objectives, setObjectives] = useState<OKRObjective[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedKPI, setSelectedKPI] = useState<KPI | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      // Fetch KPIs
      const kpiResponse = await fetch('/api/kpis')
      const kpiData = await kpiResponse.json()
      if (kpiData.success) {
        setKpis(kpiData.data)
      }

      // Fetch OKRs
      const okrResponse = await fetch('/api/okrs?quarter=Q4&year=2025')
      const okrData = await okrResponse.json()
      if (okrData.success) {
        setObjectives(okrData.data)
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getKPI = (name: string): KPI | null => {
    for (const category in kpis) {
      const kpi = kpis[category]?.find(k => k.name === name)
      if (kpi) {
        // Convert decimal strings to numbers if needed
        return {
          ...kpi,
          current_value: typeof kpi.current_value === 'string' ? parseFloat(kpi.current_value) : kpi.current_value,
          target_value: typeof kpi.target_value === 'string' ? parseFloat(kpi.target_value) : kpi.target_value,
          change_percentage: typeof kpi.change_percentage === 'string' ? parseFloat(kpi.change_percentage) : kpi.change_percentage,
        }
      }
    }
    console.log(`KPI not found: ${name}`, Object.keys(kpis))
    return null
  }

  const getKPIValue = (name: string): number => {
    const kpi = getKPI(name)
    return kpi ? kpi.current_value : 0
  }

  const calculateObjectiveProgress = (obj: OKRObjective): number => {
    if (!obj.keyResults || obj.keyResults.length === 0) return 0
    const total = obj.keyResults.reduce((sum, kr) => sum + (kr.progress_percentage || 0), 0)
    return Math.round(total / obj.keyResults.length)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-royal-blue"></div>
      </div>
    )
  }

  const totalRevenue = getKPIValue('Total Revenue')
  const signedCSAs = getKPIValue('Signed CSAs')
  const pipelineValue = getKPIValue('Pipeline Value')
  const qualifiedLeads = getKPIValue('Qualified Leads')
  const proposalsDelivered = getKPIValue('Proposals Delivered')
  const csasSent = getKPIValue('CSAs Sent')

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-dashboard-title text-royal-blue-700 mb-2">Dashboard</h1>
        <p className="text-graphite-grey-500">
          Q4 2025 Performance Overview - GCC Market Entry & Advisory Services
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div 
          onClick={() => {
            const kpi = getKPI('Total Revenue')
            if (kpi) setSelectedKPI(kpi)
          }} 
          className="cursor-pointer hover:shadow-lg transition-shadow"
        >
          <MetricCard
            title="Q4 2025 Revenue"
            value={totalRevenue}
            change={Math.round((totalRevenue / 400000) * 100)}
            trend={totalRevenue >= 280000 ? 'up' : 'neutral'}
            format="currency"
            icon={<DollarSign className="w-6 h-6" />}
            subtitle="Target: $400K · Click for details"
          />
        </div>
        <div 
          onClick={() => {
            const kpi = getKPI('Signed CSAs')
            if (kpi) setSelectedKPI(kpi)
          }} 
          className="cursor-pointer hover:shadow-lg transition-shadow"
        >
          <MetricCard
            title="Signed CSAs"
            value={signedCSAs}
            change={Math.round((signedCSAs / 5) * 100)}
            trend={signedCSAs >= 3 ? 'up' : 'neutral'}
            format="number"
            icon={<TrendingUp className="w-6 h-6" />}
            subtitle="Target: 5 · Click for details"
          />
        </div>
        <div 
          onClick={() => {
            const kpi = getKPI('Pipeline Value')
            if (kpi) setSelectedKPI(kpi)
          }} 
          className="cursor-pointer hover:shadow-lg transition-shadow"
        >
          <MetricCard
            title="Pipeline Value"
            value={pipelineValue}
            change={Math.round((pipelineValue / 1200000) * 100)}
            trend={pipelineValue >= 840000 ? 'up' : 'neutral'}
            format="currency"
            icon={<Users className="w-6 h-6" />}
            subtitle="Target: $1.2M (3x) · Click for details"
          />
        </div>
        <div 
          onClick={() => {
            const kpi = getKPI('Qualified Leads')
            if (kpi) setSelectedKPI(kpi)
          }} 
          className="cursor-pointer hover:shadow-lg transition-shadow"
        >
          <MetricCard
            title="Qualified Leads"
            value={qualifiedLeads}
            change={Math.round((qualifiedLeads / 200) * 100)}
            trend={qualifiedLeads >= 140 ? 'up' : 'neutral'}
            format="number"
            icon={<Target className="w-6 h-6" />}
            subtitle="Target: 200 · Click for details"
          />
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* OKR Progress */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Q4 2025 OKR Progress - Top Priorities</h2>
          </div>
          <div className="card-body space-y-4">
            {objectives.map((obj) => {
              const progress = calculateObjectiveProgress(obj)
              const colorClass = 
                obj.priority === 'CRITICAL' ? 'red' :
                obj.priority === 'HIGH' ? 'orange' :
                obj.priority === 'MEDIUM-HIGH' ? 'yellow' : 'blue'
              
              return (
                <div key={obj.id}>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-graphite-grey-700">
                      {obj.title} ({obj.priority})
                    </span>
                    <span className={`text-sm font-semibold text-${colorClass}-600`}>
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full bg-graphite-grey-100 rounded-full h-2">
                    <div
                      className={`bg-${colorClass}-500 h-2 rounded-full transition-all`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Active Pipeline */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Active Pipeline</h2>
          </div>
          <div className="card-body space-y-3">
            {[
              { name: 'TechVenture UAE Market Entry', value: '$50K', status: 'CSA Negotiation' },
              { name: 'HealthTech Saudi Expansion', value: '$50K', status: 'Engagement Delivered' },
              { name: 'Fintech GCC Advisory', value: '$50K', status: 'Proposal Shared' },
              { name: 'CleanEnergy Corporate Dev', value: '$50K', status: 'CSA Negotiation' },
            ].map((deal, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-pearl-white-100 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-graphite-grey-700">{deal.name}</p>
                  <p className="text-xs text-graphite-grey-500 mt-1">{deal.status}</p>
                </div>
                <span className="text-sm font-semibold text-royal-blue-600">
                  {deal.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sales Funnel */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Q4 2025 Sales Funnel</h2>
          <span className="text-sm text-graphite-grey-500">Target: $400K Revenue | 5 Signed CSAs</span>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-5 gap-4">
            <div className="text-center">
              <div className="bg-royal-blue-50 rounded-lg p-4 mb-2">
                <p className="text-2xl font-bold text-royal-blue-700">{qualifiedLeads}</p>
                <p className="text-xs text-graphite-grey-600 mt-1">of 200</p>
              </div>
              <p className="text-sm font-medium text-graphite-grey-700">Qualified Leads</p>
              <p className="text-xs text-graphite-grey-500 mt-1">{Math.round((qualifiedLeads/200)*100)}% → target</p>
            </div>
            <div className="text-center">
              <div className="bg-royal-blue-100 rounded-lg p-4 mb-2">
                <p className="text-2xl font-bold text-royal-blue-700">{proposalsDelivered}</p>
                <p className="text-xs text-graphite-grey-600 mt-1">of 30</p>
              </div>
              <p className="text-sm font-medium text-graphite-grey-700">Proposals Delivered</p>
              <p className="text-xs text-graphite-grey-500 mt-1">{Math.round((proposalsDelivered/30)*100)}% → target</p>
            </div>
            <div className="text-center">
              <div className="bg-golden-100 rounded-lg p-4 mb-2">
                <p className="text-2xl font-bold text-golden-700">{csasSent}</p>
                <p className="text-xs text-graphite-grey-600 mt-1">of 15</p>
              </div>
              <p className="text-sm font-medium text-graphite-grey-700">CSAs Sent</p>
              <p className="text-xs text-graphite-grey-500 mt-1">{Math.round((csasSent/15)*100)}% → target</p>
            </div>
            <div className="text-center">
              <div className="bg-success/10 rounded-lg p-4 mb-2">
                <p className="text-2xl font-bold text-success">{signedCSAs}</p>
                <p className="text-xs text-graphite-grey-600 mt-1">of 5</p>
              </div>
              <p className="text-sm font-medium text-graphite-grey-700">CSAs Signed</p>
              <p className="text-xs text-graphite-grey-500 mt-1">{Math.round((signedCSAs/5)*100)}% → target</p>
            </div>
            <div className="text-center">
              <div className="bg-success/10 rounded-lg p-4 mb-2">
                <p className="text-2xl font-bold text-success">$0K</p>
                <p className="text-xs text-graphite-grey-600 mt-1">of $400K</p>
              </div>
              <p className="text-sm font-medium text-graphite-grey-700">Revenue</p>
              <p className="text-xs text-graphite-grey-500 mt-1">0% → target</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Detail Modal */}
      {selectedKPI && (
        <KPIDetailModal
          kpi={selectedKPI}
          isOpen={!!selectedKPI}
          onClose={() => setSelectedKPI(null)}
        />
      )}
    </div>
  )
}
